import { logger } from "@/lib/logger";
import { useState, useRef, useEffect, useCallback } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Bot, User, Loader2, Sparkles, BarChart3, TrendingUp, DollarSign, Target } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import ReactMarkdown from "react-markdown";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function authHeaders() { const t = localStorage.getItem("roas_session_token"); return t ? { "Authorization": `Bearer ${t}` } : {}; }

const SUGGESTIONS = [
  { icon: <BarChart3 className="w-4 h-4" />, text: "Which platform has the best ROAS?" },
  { icon: <TrendingUp className="w-4 h-4" />, text: "Show me a chart of spend vs revenue by platform" },
  { icon: <DollarSign className="w-4 h-4" />, text: "Which campaigns should I pause?" },
  { icon: <Target className="w-4 h-4" />, text: "Give me a budget reallocation recommendation" },
];

const DEFAULT_COLORS = ["#10B981", "#3B82F6", "#F59E0B", "#EF4444", "#8B5CF6", "#EC4899", "#06B6D4", "#F97316"];

function parseCharts(text) {
  const parts = [];
  const chartRegex = /```chart\s*\n?([\s\S]*?)```/g;
  let lastIndex = 0;
  let match;

  while ((match = chartRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push({ type: "text", content: text.slice(lastIndex, match.index) });
    }
    try {
      parts.push({ type: "chart", content: JSON.parse(match[1].trim()) });
    } catch (e) {
      parts.push({ type: "text", content: match[0] });
    }
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push({ type: "text", content: text.slice(lastIndex) });
  }

  return parts.length > 0 ? parts : [{ type: "text", content: text }];
}

function ChartRenderer({ config }) {
  const { type, title, data, dataKeys = ["value"], colors = DEFAULT_COLORS } = config;

  return (
    <div className="my-3 p-4 bg-black/30 border border-white/10 rounded-xl">
      {title && <h4 className="text-white text-sm font-semibold mb-3">{title}</h4>}
      <div className="h-[250px]">
        <ResponsiveContainer width="100%" height="100%">
          {type === "pie" ? (
            <PieChart>
              <Pie data={data} cx="50%" cy="50%" outerRadius={90} dataKey={dataKeys[0]} label={({ name, value }) => `${name}: ${value}`}>
                {data.map((entry, i) => <Cell key={`cell-${entry.name || i}`} fill={colors[i % colors.length]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#121212", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", color: "#fff" }} />
              <Legend formatter={(v) => <span className="text-zinc-400 text-xs">{v}</span>} />
            </PieChart>
          ) : type === "line" ? (
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="name" stroke="#71717a" tick={{ fill: "#71717a", fontSize: 11 }} />
              <YAxis stroke="#71717a" tick={{ fill: "#71717a", fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "#121212", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", color: "#fff" }} />
              <Legend formatter={(v) => <span className="text-zinc-400 text-xs">{v}</span>} />
              {dataKeys.map((key, i) => <Line key={key} type="monotone" dataKey={key} stroke={colors[i % colors.length]} strokeWidth={2} dot={{ r: 3 }} />)}
            </LineChart>
          ) : (
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
              <XAxis dataKey="name" stroke="#71717a" tick={{ fill: "#71717a", fontSize: 11 }} />
              <YAxis stroke="#71717a" tick={{ fill: "#71717a", fontSize: 11 }} />
              <Tooltip contentStyle={{ background: "#121212", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "8px", color: "#fff" }} />
              <Legend formatter={(v) => <span className="text-zinc-400 text-xs">{v}</span>} />
              {dataKeys.map((key, i) => <Bar key={key} dataKey={key} fill={colors[i % colors.length]} radius={[4, 4, 0, 0]} />)}
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function MessageContent({ content }) {
  const parts = parseCharts(content);

  return (
    <div className="space-y-2">
      {parts.map((part, i) =>
        part.type === "chart" ? (
          <ChartRenderer key={`chart-${i}-${part.content?.title || ''}`} config={part.content} />
        ) : (
          <div key={`text-${i}`} className="prose prose-invert prose-sm max-w-none [&>ul]:space-y-1 [&>ol]:space-y-1 [&>p]:mb-2 [&>h3]:text-white [&>h3]:mt-3 [&>h3]:mb-1 [&>strong]:text-white">
            <ReactMarkdown>{part.content}</ReactMarkdown>
          </div>
        )
      )}
    </div>
  );
}

export default function AdvisorPage({ user }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [aiTokens, setAiTokens] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const isDemoMode = user?.is_demo;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const fetchTokens = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/ai/tokens`, { credentials: "include", headers: authHeaders() });
      if (resp.ok) setAiTokens(await resp.json());
    } catch (e) { logger.error("Error:", e); }
  }, []);

  useEffect(() => {
    fetchTokens();
  }, [fetchTokens]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;

    const userMsg = { role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const resp = await fetch(`${API}/advisor/chat`, {
        method: "POST",
        credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId }),
      });

      if (resp.ok) {
        const data = await resp.json();
        setSessionId(data.session_id);
        setMessages((prev) => [...prev, { role: "assistant", content: data.response }]);
      } else {
        setMessages((prev) => [...prev, { role: "assistant", content: "Sorry, I couldn't process that request. Please try again." }]);
      }
    } catch (e) {
      logger.error("Advisor error:", e);
      setMessages((prev) => [...prev, { role: "assistant", content: "Connection error. Please try again." }]);
    } finally {
      setLoading(false);
      fetchTokens();
      inputRef.current?.focus();
    }
  };

  return (
    <DashboardLayout user={user}>
      <div data-testid="advisor-page" className="flex flex-col h-[calc(100vh-140px)] max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <h1 className="font-heading text-2xl text-white font-bold">Advisor</h1>
              <p className="text-zinc-400 text-sm">AI-powered marketing insights for your account</p>
            </div>
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 overflow-y-auto rounded-xl border border-white/10 bg-[#0A0A0A] p-4 space-y-4 mb-4">
          {isDemoMode ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 rounded-2xl bg-zinc-800 flex items-center justify-center mb-4">
                <Sparkles className="w-8 h-8 text-zinc-500" />
              </div>
              <h2 className="text-zinc-400 text-lg font-semibold mb-1">Advisor is available in Live Mode</h2>
              <p className="text-zinc-500 text-sm max-w-md">
                Sign up for a full account to use AI-powered marketing insights on your real campaign data.
              </p>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 rounded-2xl bg-violet-500/10 flex items-center justify-center mb-4">
                <Sparkles className="w-8 h-8 text-violet-400" />
              </div>
              <h2 className="text-white text-lg font-semibold mb-1">Ask your Advisor</h2>
              <p className="text-zinc-400 text-sm mb-6 max-w-md">
                I analyze your campaigns, budgets, and performance data to give you actionable marketing insights.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-lg">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s.text}
                    onClick={() => sendMessage(s.text)}
                    className="flex items-center gap-2 p-3 bg-white/5 border border-white/10 rounded-lg text-left text-sm text-zinc-300 hover:bg-violet-500/10 hover:border-violet-500/30 transition-colors"
                  >
                    <span className="text-violet-400">{s.icon}</span>
                    {s.text}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              <AnimatePresence>
                {messages.map((msg, i) => (
                  <motion.div
                    key={`msg-${msg.role}-${i}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={cn("flex gap-3", msg.role === "user" ? "justify-end" : "justify-start")}
                  >
                    {msg.role === "assistant" && (
                      <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                        <Bot className="w-4 h-4 text-violet-400" />
                      </div>
                    )}
                    <div className={cn(
                      "max-w-[85%] rounded-xl px-4 py-3",
                      msg.role === "user"
                        ? "bg-violet-600 text-white"
                        : "bg-[#121212] border border-white/10 text-zinc-300"
                    )}>
                      {msg.role === "user" ? (
                        <p className="text-sm">{msg.content}</p>
                      ) : (
                        <MessageContent content={msg.content} />
                      )}
                    </div>
                    {msg.role === "user" && (
                      <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0 mt-1">
                        <User className="w-4 h-4 text-zinc-400" />
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
              {loading && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-3">
                  <div className="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-violet-400" />
                  </div>
                  <div className="bg-[#121212] border border-white/10 rounded-xl px-4 py-3">
                    <div className="flex items-center gap-2 text-zinc-400 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Analyzing your data...
                    </div>
                  </div>
                </motion.div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Input */}
        <div className="flex gap-2">
          <Input
            ref={inputRef}
            data-testid="advisor-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !isDemoMode && sendMessage(input)}
            placeholder={isDemoMode ? "Advisor is available in Live Mode" : "Ask about your campaigns, performance, budget allocation..."}
            className="bg-[#121212] border-white/10 text-white placeholder:text-zinc-500 h-12"
            disabled={loading || isDemoMode}
          />
          <Button
            data-testid="advisor-send-btn"
            onClick={() => sendMessage(input)}
            disabled={!input.trim() || loading || isDemoMode}
            className="bg-violet-600 hover:bg-violet-700 text-white h-12 px-5"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-zinc-500 text-xs text-center mt-2">
          Powered by AI. Advisor can make mistakes — please verify insights before making decisions.
        </p>
      </div>
    </DashboardLayout>
  );
}
